/**
 * KNOWLEDGE BASE — Grup Xaluca
 * ----------------------------------------------------------------------
 * This file is the SINGLE SOURCE OF TRUTH for project content.
 * Every section the user shares gets appended to the appropriate
 * category here. The /knowledgebase page renders it directly.
 *
 * To add new content:
 *   1. Find the right category in CATEGORIES (or add a new one).
 *   2. Append a new section object with { id, title, paragraphs?, list?, raw? }.
 *   3. Keep `id`s in kebab-case and globally unique — they become URL hashes.
 *
 * NOTHING IN THIS FILE IS RENDERED OUTSIDE /knowledgebase YET.
 * It is intentionally decoupled from the public site copy.
 */

export const KB_META = {
  title: "Knowledge Base",
  tagline: "Grup Xaluca · Fuente única de información del proyecto",
  intro:
    "Repositorio editorial centralizado de todo el contenido del grupo: la empresa, los destinos, los hoteles, los bivouacs, las experiencias y los servicios. Esta página crecerá con el tiempo a medida que se incorpore nueva información.",
  lastUpdated: "2026-03-06",
};

/**
 * Each CATEGORY has:
 *   id        — kebab-case, unique. Used as URL hash.
 *   label     — short label shown in the TOC.
 *   icon      — lucide-react icon name (optional).
 *   sections  — array of section objects.
 *
 * Each SECTION has:
 *   id           — kebab-case, unique site-wide. URL hash.
 *   title        — heading rendered in the article.
 *   subtitle?    — optional small kicker shown above the title.
 *   paragraphs?  — array of prose strings (rendered as <p>).
 *   list?        — array of { name?, body } bullet items.
 *   raw?         — array of pre-formatted blocks (e.g. notes, code).
 *   subsections? — array of nested blocks for deep-dive content
 *                  ({ id, title, paragraphs?, list?, raw? }). Each gets
 *                  its own anchor and is shown nested in the TOC.
 */
export const CATEGORIES = [
  {
    id: "bienvenida",
    label: "Bienvenida",
    icon: "Sparkles",
    sections: [
      {
        id: "intro-bienvenida",
        title: "Hoteles, Bivouacs, Eventos y Servicios en el Sur de Marruecos",
        paragraphs: [
          "En nombre de Grup Xaluca, te damos nuestra más cordial bienvenida y queremos expresar nuestro sincero agradecimiento por tu interés en conocer más sobre nuestro grupo de hoteles y servicios en la zona del sur de Marruecos.",
          "A través de la documentación que ponemos a tu disposición, podrás descubrir nuestro apasionante mundo de hospitalidad en el sur de Marruecos. Cada uno de nuestros hoteles y establecimientos ha sido creado con el amor y la dedicación que caracteriza a una empresa familiar que lleva más de 30 años construyendo experiencias únicas en esta fascinante región.",
          "En nuestra knowledge base encontrarás información detallada sobre nuestro completo portfolio de alojamientos y servicios, cuidadosamente diseñados para ofrecerte una experiencia auténtica del Marruecos más genuino. Desde hoteles boutique hasta servicios personalizados, cada elemento de nuestra oferta refleja nuestro compromiso con la excelencia y la autenticidad.",
          "Te invitamos a explorar toda la información y no dudes en contactarnos ante cualquier consulta. Será un placer atenderte y ayudarte a planificar tu experiencia con Grup Xaluca en el sur de Marruecos.",
        ],
      },
      {
        id: "descubre-el-alma",
        title: "Descubre el alma del Sur de Marruecos con Grup Xaluca",
        paragraphs: [
          "Imagínate despertar en un lugar donde el tiempo fluye a otro ritmo, donde cada amanecer pinta el cielo de tonos dorados sobre un mar infinito de dunas y cada atardecer se desvanece majestuosamente entre las imponentes cumbres del Atlas. Te damos la bienvenida al Sur de Marruecos, una tierra de paisajes que cautivan el alma, donde la cultura ancestral vibra en cada rincón y la hospitalidad se vive como un arte milenario. Bienvenido al mundo de Grup Xaluca.",
          "Durante más de tres décadas, Grup Xaluca ha sido el guardián de los secretos del desierto, tendiendo puentes entre viajeros y la magia del Sáhara. No solo ofrecemos alojamientos excepcionales, sino que creamos experiencias genuinas que transforman cada viaje en una aventura que permanecerá grabada en tu memoria para siempre.",
        ],
      },
    ],
  },

  {
    id: "destinos",
    label: "Destinos",
    icon: "MapPin",
    sections: [
      {
        id: "esencia-sur-marruecos",
        title: "La esencia del sur de Marruecos",
        subtitle: "Entre montañas, oasis y dunas",
        paragraphs: [
          "El Sur de Marruecos es un territorio de contrastes: desde los imponentes picos del Alto Atlas hasta las dunas doradas del Erg Chebbi, pasando por palmerales infinitos y antiguas kasbahs que cuentan historias de caravanas y exploradores.",
          "Cada rincón de esta región tiene un alma propia, y en Grup Xaluca hemos diseñado nuestra oferta para que cada visitante viva su propia historia. Desde la mística ciudad de Arfoud, puerta de entrada al desierto, hasta las dunas de Merzouga, donde el tiempo parece detenerse bajo un cielo estrellado, y las impresionantes Gargantas del Dades, hogar de comunidades bereberes que conservan su esencia ancestral.",
        ],
      },
    ],
  },

  {
    id: "hoteles",
    label: "Hoteles",
    icon: "BedDouble",
    sections: [
      {
        id: "hoteles-intro",
        title: "Hoteles que capturan la esencia del sur de Marruecos",
        paragraphs: [
          "En cada uno de nuestros hoteles y campamentos, combinamos el confort y la tradición para ofrecer una estancia única en el sur de Marruecos.",
        ],
      },
      {
        id: "kasbah-hotel-xaluca-arfoud",
        title: "Kasbah Hotel Xaluca Arfoud",
        subtitle: "La puerta del desierto · Arfoud",
        paragraphs: [
          "Ubicado en la histórica ciudad de Arfoud, este hotel de estilo kasbah, construido con la tradicional técnica de adobe, es el refugio perfecto para quienes desean explorar la magia del desierto sin renunciar a la comodidad. Con 200 habitaciones exquisitamente decoradas, spa, piscina y una oferta gastronómica de primer nivel, la Kasbah Hotel Xaluca Arfoud es la base ideal para adentrarse en el sur de Marruecos.",
        ],
        subsections: [
          {
            id: "kasbah-xaluca-vision-general",
            title: "Visión general",
            list: [
              { name: "Nombre oficial", body: "Kasbah Hotel Xaluca." },
              {
                name: "Ubicación",
                body: "Arfoud (Erfoud), provincia de Errachidia, región de Drâa-Tafilalet, Marruecos.",
              },
              {
                name: "Concepto",
                body: "Uno de los hoteles emblemáticos de Grup Xaluca y la principal puerta de entrada al desierto del Sáhara. Construido siguiendo la arquitectura tradicional marroquí en adobe, combina la autenticidad de una gran kasbah con instalaciones modernas, amplios jardines, spa, piscinas y una completa oferta de actividades para descubrir el desierto.",
              },
            ],
          },
          {
            id: "kasbah-xaluca-posicionamiento",
            title: "Posicionamiento",
            list: [
              { body: "Hotel de referencia en el sur de Marruecos." },
              { body: "Puerta de entrada al desierto del Erg Chebbi." },
              { body: "Ideal para viajes de aventura, escapadas, circuitos, grupos, incentivos y eventos." },
            ],
          },
          {
            id: "kasbah-xaluca-datos-principales",
            title: "Datos principales",
            list: [
              { name: "Región", body: "Drâa-Tafilalet." },
              { name: "Provincia", body: "Errachidia." },
              { name: "Localidad", body: "Arfoud (Erfoud)." },
              { name: "Aeropuerto", body: "A 65 km del Aeropuerto de Errachidia." },
              { name: "Centro de Arfoud", body: "A 6 km." },
            ],
          },
          {
            id: "kasbah-xaluca-habitaciones",
            title: "Habitaciones",
            paragraphs: ["Capacidad total: 200 habitaciones."],
            list: [
              { name: "Tipologías", body: "Habitación Doble, Habitación Twin, Habitación Familiar, Junior Suite, Senior Suite, Royal Suite, Bungalow." },
              {
                name: "Bungalows",
                body: "Ofrecen el máximo nivel de privacidad dentro del complejo. Entrada independiente, aproximadamente 120 m², cocina, salón, dos suites con baño privado y jacuzzi, patio interior y terraza privada. Los huéspedes disfrutan además de acceso completo a todos los servicios del hotel.",
              },
            ],
          },
          {
            id: "kasbah-xaluca-restauracion",
            title: "Restauración",
            paragraphs: [
              "El hotel dispone también de buffet junto a la piscina y salón para banquetes con capacidad para aproximadamente 300 personas.",
            ],
            list: [
              { name: "Restaurantes", body: "Restaurant Sijilmassa · Restaurant Bladi · Restaurant Tizimi · Restaurant Mabroka." },
              { name: "Bares", body: "Bar Tataoui · Bar Sahara." },
            ],
          },
          {
            id: "kasbah-xaluca-spa-wellness",
            title: "Spa & Wellness",
            list: [
              { body: "Hammam tradicional." },
              { body: "Sauna." },
              { body: "9 salas de masaje." },
              { body: "Duchas Vichy." },
              { body: "Máquina de hielo." },
              { body: "Zona de relajación." },
            ],
          },
          {
            id: "kasbah-xaluca-piscinas",
            title: "Piscinas",
            list: [
              { body: "Piscina exterior con terraza y bar." },
              { body: "Piscina interior climatizada." },
              { body: "Jacuzzi." },
              { body: "Zona de relax." },
            ],
          },
          {
            id: "kasbah-xaluca-deporte",
            title: "Instalaciones deportivas",
            list: [
              { body: "Gimnasio." },
              { body: "Pista de tenis." },
              { body: "Pista de pádel." },
              { body: "Campo de fútbol." },
              { body: "Minigolf." },
              { body: "Ping-pong." },
              { body: "Petanca." },
            ],
          },
          {
            id: "kasbah-xaluca-eventos-congresos",
            title: "Eventos y congresos",
            paragraphs: [
              "El hotel dispone de un salón de congresos con capacidad aproximada para 250 asistentes, equipado para reuniones, incentivos, congresos, bodas y celebraciones.",
            ],
          },
          {
            id: "kasbah-xaluca-tienda",
            title: "Tienda",
            paragraphs: [
              "Tienda especializada en artesanía marroquí, fósiles, antigüedades, joyería, textiles y productos tradicionales de la región.",
            ],
          },
          {
            id: "kasbah-xaluca-actividades",
            title: "Actividades",
            list: [
              { name: "Organizadas por el hotel", body: "Excursiones en 4x4 · Paseos en dromedario · Rutas en quad · Experiencias por el desierto · Excursiones privadas · Actividades culturales." },
            ],
          },
          {
            id: "kasbah-xaluca-alrededores",
            title: "Qué hacer en los alrededores",
            paragraphs: [
              "Los huéspedes pueden disfrutar de numerosas experiencias cerca del hotel:",
            ],
            list: [
              { body: "Erg Chebbi." },
              { body: "Paseos en dromedario." },
              { body: "Sandboarding." },
              { body: "Rutas en 4x4." },
              { body: "Pueblo de Khamlia." },
              { body: "Minas de M'Fis." },
              { body: "Oasis del desierto." },
              { body: "Encuentro con familias nómadas." },
              { body: "Talleres de fósiles." },
              { body: "Kasbahs tradicionales." },
              { body: "Mercado de Rissani." },
              { body: "Observación de estrellas." },
              { body: "Valle del Ziz." },
              { body: "Lago Dayet Sriji." },
              { body: "Ruta de las Mil Kasbahs." },
              { body: "Festival de los Dátiles de Erfoud." },
            ],
          },
          {
            id: "kasbah-xaluca-perfil-huesped",
            title: "Perfil del huésped",
            paragraphs: ["Kasbah Hotel Xaluca está orientado a:"],
            list: [
              { body: "Viajeros que descubren el Sáhara por primera vez." },
              { body: "Parejas." },
              { body: "Familias." },
              { body: "Grupos organizados." },
              { body: "Viajes de incentivo." },
              { body: "Eventos corporativos." },
              { body: "Amantes de la aventura y la cultura marroquí." },
            ],
          },
          {
            id: "kasbah-xaluca-diferenciadores",
            title: "Diferenciadores",
            list: [
              { body: "Arquitectura tradicional de kasbah marroquí." },
              { body: "Uno de los hoteles de referencia del desierto." },
              { body: "Amplias instalaciones." },
              { body: "Gran oferta gastronómica." },
              { body: "Spa & Wellness." },
              { body: "Excelente punto de partida para descubrir Erg Chebbi y el sur de Marruecos." },
              { body: "Amplia oferta de actividades y experiencias." },
            ],
          },
        ],
      },
      {
        id: "kasbah-hotel-tombouctou",
        title: "Kasbah Hotel Tombouctou",
        subtitle: "Autenticidad y confort en el corazón del desierto · Erg Chebbi · Merzouga",
        paragraphs: [
          "Un desierto puede parecer un lugar inhóspito a primera vista, pero quienes se toman el tiempo de explorarlo descubren que está lleno de magia. Es un lugar donde el tiempo parece detenerse, donde el silencio invita a la reflexión y donde cada amanecer y cada atardecer transforman el paisaje en un espectáculo inolvidable.",
          "La Kasbah Hotel Tombouctou se ha convertido en uno de los establecimientos de referencia del desierto del Erg Chebbi, ofreciendo una combinación única entre hospitalidad tradicional, arquitectura marroquí y todas las comodidades necesarias para disfrutar plenamente del Sahara.",
          "Situado frente a las dunas más espectaculares de Marruecos, el hotel constituye una auténtica puerta de entrada al desierto y al universo de Grup Xaluca.",
          "Su arquitectura, inspirada en las antiguas kasbahs del Sahara, combina materiales tradicionales, líneas sencillas y espacios cálidos que transmiten la esencia del sur de Marruecos.",
          "Con 77 habitaciones, decoradas con materiales naturales, revestimientos tradicionales de tadelakt, colores cálidos y artesanía local, el hotel ofrece un refugio de tranquilidad en medio de uno de los paisajes más impresionantes del norte de África.",
          "Maati Ettaiek · Director.",
        ],
        subsections: [
          {
            id: "tombouctou-tadelakt",
            title: "El Tadelakt",
            paragraphs: [
              "Uno de los elementos más característicos de la arquitectura del hotel es el uso del tadelakt, una técnica tradicional marroquí utilizada desde hace siglos. Su acabado es suave, satinado y ligeramente brillante, aportando elegancia y una excelente resistencia a la humedad.",
              "El tadelakt continúa siendo uno de los elementos arquitectónicos más representativos de Marruecos y constituye una parte esencial del diseño interior de Kasbah Hotel Tombouctou.",
            ],
            list: [
              { body: "Cal artesanal." },
              { body: "Pigmentos minerales." },
              { body: "Jabón negro." },
              { body: "Materiales completamente naturales." },
            ],
          },
          {
            id: "tombouctou-datos",
            title: "Un hotel en pleno Erg Chebbi",
            paragraphs: [
              "Kasbah Hotel Tombouctou se encuentra en una ubicación privilegiada, frente a las dunas del Erg Chebbi. Desde aquí parten la mayoría de actividades organizadas por Grup Xaluca para descubrir el Sahara.",
            ],
            list: [
              { name: "Región", body: "Merzouga." },
              { name: "Entorno", body: "Erg Chebbi." },
              { name: "Habitaciones", body: "77." },
              { name: "Distancia a Merzouga", body: "6 km." },
              { name: "Aeropuerto", body: "A 130 km del Aeropuerto de Errachidia." },
            ],
          },
          {
            id: "tombouctou-historia",
            title: "Una historia ligada al desierto",
            paragraphs: [
              "Kasbah Hotel Tombouctou fue el primer establecimiento de Grup Xaluca construido junto a las dunas del Erg Chebbi.",
              "Lo que comenzó siendo un pequeño alojamiento ha evolucionado hasta convertirse en uno de los hoteles más reconocidos del desierto marroquí.",
              "Su construcción mantiene intacto el espíritu de las antiguas fortalezas bereberes, incorporando todas las comodidades necesarias para el viajero actual.",
              "Uno de sus elementos más emblemáticos es la gran puerta de entrada decorada con siluetas de dromedarios, símbolo de la hospitalidad y de la cultura nómada del Sahara.",
            ],
          },
          {
            id: "tombouctou-habitaciones",
            title: "Habitaciones",
            paragraphs: [
              "Las 77 habitaciones del hotel han sido diseñadas para integrarse perfectamente en el paisaje. Cada estancia transmite una sensación de calma y autenticidad.",
            ],
            list: [
              { body: "Colores arena." },
              { body: "Piedra natural." },
              { body: "Madera." },
              { body: "Artesanía bereber." },
              { body: "Decoración tradicional." },
            ],
          },
          {
            id: "tombouctou-standard",
            title: "Habitaciones Standard",
            paragraphs: [
              "Las habitaciones Standard ofrecen un ambiente acogedor después de una jornada de exploración. Su decoración reproduce los tonos cálidos del Sahara.",
            ],
            list: [
              { body: "Cama doble o dos camas individuales." },
              { body: "Baño completo." },
              { body: "Aire acondicionado." },
              { body: "Internet." },
              { body: "Televisión vía satélite." },
              { body: "Minibar." },
            ],
          },
          {
            id: "tombouctou-suites",
            title: "Suites",
            paragraphs: [
              "Las Suites ofrecen una experiencia superior gracias a su mayor amplitud. La decoración combina elegancia contemporánea y tradición marroquí.",
            ],
            list: [
              { body: "Dormitorio amplio." },
              { body: "Cama King Size." },
              { body: "Zona de estar." },
              { body: "Baño completo." },
              { body: "Aire acondicionado." },
              { body: "Televisión vía satélite." },
              { body: "Internet." },
              { body: "Minibar." },
            ],
          },
          {
            id: "tombouctou-suite-royal",
            title: "Suite Royal",
            paragraphs: [
              "La Suite Royal representa la máxima expresión del confort dentro del hotel. Se trata de un espectacular dúplex diseñado para quienes buscan una estancia absolutamente exclusiva en el desierto.",
            ],
            list: [
              { body: "Amplio salón." },
              { body: "Dos dormitorios." },
              { body: "Jacuzzi privado." },
              { body: "Vistas panorámicas al Erg Chebbi." },
              { body: "Barra privada." },
              { body: "Minibar." },
              { body: "Zona de descanso." },
            ],
          },
          {
            id: "tombouctou-gastronomia",
            title: "Gastronomía · Sabores del Sahara",
            paragraphs: [
              "La propuesta gastronómica de Kasbah Hotel Tombouctou combina recetas tradicionales marroquíes con cocina internacional. Cada comida se convierte en una experiencia donde los sabores tradicionales conviven con una cuidada selección de productos locales.",
            ],
            list: [
              { name: "Capacidad", body: "Para 200 personas." },
              { name: "Espacios", body: "Diferentes salones · Climatización · Vistas panorámicas al desierto." },
              { name: "Especialidades", body: "Tajines · Cuscús · Pastela · Cocina bereber · Cocina internacional." },
            ],
          },
          {
            id: "tombouctou-chill-out",
            title: "Zonas Chill Out",
            paragraphs: [
              "Después de la cena, los huéspedes pueden relajarse en las diferentes zonas de descanso del hotel. Todo ello con vistas privilegiadas al Erg Chebbi.",
            ],
            list: [
              { body: "Bar principal." },
              { body: "Terrazas." },
              { body: "Espacios lounge." },
              { body: "Zonas exteriores." },
            ],
          },
          {
            id: "tombouctou-bar-piscina",
            title: "Bar de la Piscina",
            paragraphs: [
              "Durante el día, la piscina constituye uno de los espacios más animados del hotel. Un lugar perfecto para relajarse tras una jornada de excursiones.",
            ],
            list: [
              { body: "Bar." },
              { body: "Tumbonas." },
              { body: "Sombrillas." },
              { body: "Servicio de bebidas." },
            ],
          },
          {
            id: "tombouctou-spa",
            title: "Spa & Wellness",
            paragraphs: [
              "En pleno corazón del Sahara, el Spa del hotel ofrece un auténtico espacio dedicado al bienestar. Todos los tratamientos utilizan técnicas tradicionales marroquíes combinadas con productos naturales cuidadosamente seleccionados. El objetivo es recuperar cuerpo y mente después de un intenso día de aventura por el desierto.",
            ],
            list: [
              { body: "Salas de masaje." },
              { body: "Hammam tradicional." },
              { body: "Sauna." },
              { body: "Jacuzzi." },
              { body: "Tratamientos faciales." },
              { body: "Tratamientos corporales." },
            ],
          },
          {
            id: "tombouctou-actividades",
            title: "Actividades en el Desierto",
            paragraphs: [
              "Kasbah Hotel Tombouctou constituye el punto de partida ideal para descubrir el Sahara mediante experiencias organizadas por los equipos locales de Grup Xaluca. Cada actividad está diseñada para mostrar la auténtica esencia del desierto, combinando aventura, cultura y naturaleza.",
            ],
          },
          {
            id: "tombouctou-4x4",
            title: "Excursiones en 4x4",
            paragraphs: [
              "Una de las actividades más solicitadas es recorrer el Erg Chebbi en vehículos todoterreno. Las rutas permiten acceder a lugares inaccesibles para vehículos convencionales y descubrir algunos de los paisajes más sorprendentes del Sahara. Todos los recorridos son realizados por conductores autorizados con un profundo conocimiento del desierto.",
            ],
            list: [
              { body: "Palmerales ocultos." },
              { body: "Oasis naturales." },
              { body: "Aldeas bereberes." },
              { body: "Minas de M'Fis." },
              { body: "Lago Dayet Srij." },
              { body: "Familias nómadas." },
              { body: "Khamlia." },
            ],
          },
          {
            id: "tombouctou-khamlia",
            title: "Khamlia · El pueblo de la música Gnawa",
            paragraphs: [
              "Uno de los lugares imprescindibles durante las excursiones es Khamlia. Este pequeño pueblo está habitado por descendientes de antiguas comunidades procedentes del África subsahariana.",
              "Su principal seña de identidad es la música Gnawa, declarada Patrimonio Cultural Inmaterial de la Humanidad.",
              "Durante la visita los viajeros pueden disfrutar de actuaciones tradicionales donde los tambores, los cánticos y los instrumentos ancestrales crean una experiencia profundamente emocional.",
            ],
          },
          {
            id: "tombouctou-minas-mfis",
            title: "Minas de M'Fis",
            paragraphs: [
              "Otro de los lugares más interesantes es el antiguo poblado minero de M'Fis. Durante décadas esta zona fue uno de los principales centros de extracción de plomo y cuarzo del sur de Marruecos.",
              "Actualmente las minas permanecen abandonadas, convirtiéndose en un escenario muy singular para comprender la historia reciente de la región. Desde los antiguos edificios se obtienen además excelentes vistas sobre el Erg Chebbi.",
            ],
          },
          {
            id: "tombouctou-nomadas",
            title: "Encuentro con familias nómadas",
            paragraphs: [
              "Una de las experiencias más enriquecedoras consiste en visitar a las familias nómadas que todavía viven en el desierto. Habitualmente la visita finaliza compartiendo un tradicional té a la menta.",
            ],
            list: [
              { body: "Su modo de vida." },
              { body: "Sus costumbres." },
              { body: "La construcción de las haimas." },
              { body: "La cría de dromedarios y cabras." },
              { body: "La hospitalidad bereber." },
            ],
          },
          {
            id: "tombouctou-dromedario",
            title: "Paseos en Dromedario",
            paragraphs: [
              "Recorrer las dunas montado en un dromedario constituye una de las imágenes más representativas del Sahara. Los paseos siguen antiguas rutas caravaneras utilizadas durante siglos por comerciantes y pueblos nómadas.",
            ],
          },
          {
            id: "tombouctou-amanecer",
            title: "Amanecer",
            paragraphs: [
              "El desierto despierta lentamente mientras el sol comienza a iluminar las dunas. La temperatura es agradable y la luz ofrece unas condiciones extraordinarias para la fotografía.",
            ],
          },
          {
            id: "tombouctou-atardecer",
            title: "Atardecer",
            paragraphs: [
              "El momento más solicitado. Las dunas adquieren tonalidades doradas, rojizas y anaranjadas mientras el sol desaparece lentamente tras el horizonte. Es una de las experiencias más inolvidables del viaje.",
            ],
          },
          {
            id: "tombouctou-bivouac",
            title: "Noches en Bivouac",
            paragraphs: [
              "Dormir en el desierto constituye una experiencia completamente diferente a cualquier alojamiento convencional. Después del paseo en dromedario o en 4x4, los viajeros llegan a los campamentos donde les espera una auténtica noche bereber. Todo ello rodeado únicamente por arena y silencio.",
            ],
            list: [
              { body: "Té de bienvenida." },
              { body: "Cena tradicional." },
              { body: "Música alrededor de la hoguera." },
              { body: "Canto bereber." },
              { body: "Observación de estrellas." },
              { body: "Amanecer sobre las dunas." },
            ],
          },
          {
            id: "tombouctou-merzouga-motor",
            title: "Merzouga · Un paraíso para los amantes del motor",
            paragraphs: [
              "El Erg Chebbi ofrece algunos de los mejores recorridos de Marruecos para vehículos todoterreno.",
            ],
          },
          {
            id: "tombouctou-quad",
            title: "Excursiones en Quad",
            paragraphs: [
              "Los quads permiten recorrer rápidamente las dunas y descubrir diferentes paisajes del desierto. Las rutas están adaptadas tanto para principiantes como para usuarios experimentados. Todo el material de seguridad es proporcionado por Grup Xaluca.",
            ],
          },
          {
            id: "tombouctou-buggy",
            title: "Excursiones en Buggy",
            paragraphs: [
              "Los buggies representan una alternativa deportiva para recorrer el Erg Chebbi. Una experiencia especialmente recomendada para quienes buscan mayor adrenalina.",
            ],
            list: [
              { body: "Grandes dunas." },
              { body: "Pistas rápidas." },
              { body: "Hamadas." },
              { body: "Palmerales." },
            ],
          },
          {
            id: "tombouctou-moto",
            title: "Rutas en Moto",
            paragraphs: [
              "Merzouga se ha convertido también en uno de los principales destinos del norte de África para los viajeros que recorren Marruecos en motocicleta. Las pistas del Sahara ofrecen algunos de los itinerarios más espectaculares del continente.",
            ],
          },
          {
            id: "tombouctou-banos-arena",
            title: "Baños de Arena",
            paragraphs: [
              "Entre los tratamientos tradicionales más curiosos del desierto destacan los baños de arena. La técnica consiste en permanecer parcialmente cubierto por arena caliente durante aproximadamente entre doce y dieciséis minutos.",
              "Más allá de sus posibles beneficios físicos, constituye una experiencia profundamente vinculada a la cultura bereber.",
            ],
            list: [
              { body: "Dolores musculares." },
              { body: "Artritis." },
              { body: "Reumatismo." },
              { body: "Problemas articulares." },
            ],
          },
          {
            id: "tombouctou-cierre",
            title: "Un destino para descubrir el Sahara",
            paragraphs: [
              "Kasbah Hotel Tombouctou no es únicamente un alojamiento. Es un punto de encuentro entre el viajero y uno de los paisajes más fascinantes del planeta.",
              "Desde aquí comienza el descubrimiento del verdadero Sahara: sus dunas, sus pueblos, sus tradiciones, su gastronomía, su cultura y su silencio.",
              "Cada estancia representa una oportunidad para comprender que el desierto no es un espacio vacío, sino un territorio lleno de historia, vida y emociones.",
              "Kasbah Hotel Tombouctou invita a vivir el Sahara con calma, autenticidad y respeto, ofreciendo una experiencia que permanece para siempre en la memoria de quienes lo visitan.",
            ],
          },
        ],
      },
      {
        id: "hotel-xaluca-dades",
        title: "Hotel Xaluca Dades",
        subtitle: "Un sueño entre valles y montañas · Boumalne Dades",
        paragraphs: [
          "Hay lugares donde el paisaje invita a detenerse, respirar profundamente y redescubrir el placer de viajar sin prisas. El Valle del Dades, rodeado por las imponentes montañas del Alto Atlas, es uno de esos lugares.",
          "Aquí, entre gargantas esculpidas por millones de años de erosión, palmerales, aldeas bereberes y espectaculares formaciones rocosas, se encuentra Hotel Xaluca Dades.",
          "Antiguamente conocido como el Parador Nacional del Dades, este establecimiento ha sido completamente renovado para convertirse en uno de los hoteles más singulares del Atlas marroquí.",
          "Con una decoración inspirada en diferentes culturas africanas y una ubicación privilegiada a 1.612 metros de altitud, el hotel combina naturaleza, confort y hospitalidad en un entorno verdaderamente excepcional.",
          "Odmane Batarchi · Director.",
        ],
        subsections: [
          {
            id: "dades-datos-principales",
            title: "Datos principales",
            paragraphs: [
              "Situado sobre el Valle de los Pájaros, constituye uno de los mejores puntos de partida para descubrir el Alto Atlas.",
            ],
            list: [
              { name: "Región", body: "Drâa-Tafilalet." },
              { name: "Provincia", body: "Tinghir." },
              { name: "Localidad", body: "Boumalne Dades." },
              { name: "Altitud", body: "1.612 metros." },
              { name: "Habitaciones", body: "106." },
              { name: "Distancia", body: "A 114 km del Aeropuerto de Ouarzazate." },
            ],
          },
          {
            id: "dades-entorno",
            title: "Un refugio en el corazón del Atlas",
            paragraphs: [
              "El Hotel Xaluca Dades está rodeado por algunos de los paisajes más espectaculares de Marruecos. Desde el propio hotel es posible contemplar:",
            ],
            list: [
              { body: "Las Gargantas del Dades." },
              { body: "El Valle de las Rosas." },
              { body: "Palmerales centenarios." },
              { body: "Aldeas bereberes." },
              { body: "Impresionantes formaciones rocosas." },
            ],
          },
          {
            id: "dades-habitaciones",
            title: "Habitaciones",
            paragraphs: [
              "El hotel dispone de 106 habitaciones, decoradas con mobiliario y elementos artesanales procedentes de distintos países africanos. La combinación entre piedra, madera, tejidos tradicionales y piezas de artesanía crea una atmósfera cálida y acogedora.",
            ],
          },
          {
            id: "dades-standard",
            title: "Habitaciones Standard",
            paragraphs: [
              "Las habitaciones Standard ofrecen todas las comodidades necesarias para una estancia confortable. La decoración incorpora elementos tradicionales africanos que aportan personalidad y autenticidad a cada estancia.",
            ],
            list: [
              { body: "Baño completo." },
              { body: "Aire acondicionado." },
              { body: "Calefacción." },
              { body: "Televisión vía satélite." },
              { body: "Internet." },
              { body: "Minibar." },
              { body: "Teléfono." },
            ],
          },
          {
            id: "dades-triples",
            title: "Habitaciones Triples",
            paragraphs: [
              "Ideales para familias o pequeños grupos. Conservan el mismo estilo decorativo inspirado en la cultura africana.",
            ],
            list: [
              { body: "Tres camas individuales." },
              { body: "Baño completo." },
              { body: "Aire acondicionado." },
              { body: "Internet." },
              { body: "Minibar." },
              { body: "Televisión vía satélite." },
            ],
          },
          {
            id: "dades-suites",
            title: "Suites",
            paragraphs: [
              "Las Suites representan la categoría más exclusiva del hotel. Un espacio diseñado para disfrutar plenamente del paisaje del Alto Atlas.",
            ],
            list: [
              { body: "Amplio dormitorio." },
              { body: "Salón privado." },
              { body: "Cama King Size." },
              { body: "Terraza privada." },
              { body: "Baño completo." },
              { body: "Aire acondicionado." },
              { body: "Internet." },
              { body: "Minibar." },
              { body: "Televisión vía satélite." },
            ],
          },
          {
            id: "dades-spa-hammam",
            title: "Spa & Hammam",
            paragraphs: [
              "Después de una jornada de senderismo o excursiones por las montañas, el Spa constituye el lugar ideal para relajarse. Una experiencia de bienestar inspirada en las tradiciones marroquíes.",
            ],
            list: [
              { body: "4 salas de masaje." },
              { body: "Hammam." },
              { body: "Sauna." },
              { body: "Jacuzzi." },
              { body: "Duchas." },
              { body: "Vestuarios." },
            ],
          },
          {
            id: "dades-restauracion",
            title: "Restauración",
            paragraphs: [
              "El restaurante del Hotel Xaluca Dades ofrece una propuesta gastronómica basada en la cocina tradicional marroquí y la cocina internacional.",
            ],
            list: [
              { name: "Espacio", body: "Gran comedor panorámico." },
              { name: "Capacidad", body: "Para 220 personas." },
              { name: "Vistas", body: "Sobre el valle." },
              { name: "Especialidades", body: "Tajines · Cuscús · Cocina bereber · Platos internacionales." },
            ],
          },
          {
            id: "dades-piscina",
            title: "Piscina",
            paragraphs: [
              "La piscina exterior constituye uno de los espacios más agradables del hotel, rodeada por las montañas del Atlas.",
            ],
            list: [
              { body: "Amplia terraza." },
              { body: "Solárium." },
              { body: "Jacuzzi." },
              { body: "Bar." },
              { body: "Tumbonas." },
            ],
          },
          {
            id: "dades-deporte",
            title: "Área deportiva",
            paragraphs: [
              "Para quienes desean mantenerse activos durante su estancia, el hotel dispone de:",
            ],
            list: [
              { body: "Gimnasio." },
              { body: "Pista de tenis." },
              { body: "Pista de petanca." },
            ],
          },
          {
            id: "dades-boutique",
            title: "Boutique",
            paragraphs: [
              "La boutique del hotel reúne una cuidada selección de artesanía bereber. Cada pieza ha sido elaborada por artesanos de la región.",
            ],
            list: [
              { body: "Alfombras." },
              { body: "Lámparas." },
              { body: "Cerámica." },
              { body: "Plata bereber." },
              { body: "Collares tradicionales." },
              { body: "Objetos decorativos." },
              { body: "Textiles africanos." },
            ],
          },
          {
            id: "dades-congresos",
            title: "Salón de Congresos",
            paragraphs: [
              "El Hotel Xaluca Dades dispone de un moderno salón de congresos preparado para organizar convenciones, presentaciones, reuniones, incentivos y eventos privados, acompañado por servicios de restauración y apoyo técnico.",
            ],
          },
          {
            id: "dades-actividades-4x4",
            title: "Excursiones en 4x4",
            paragraphs: [
              "Disponemos de una flota de vehículos todoterreno para descubrir el entorno. Las rutas se adaptan al nivel y los intereses de cada viajero.",
            ],
            list: [
              { body: "Valle del Dades." },
              { body: "Valle de las Rosas." },
              { body: "Pueblos bereberes." },
              { body: "Palmerales." },
              { body: "Pistas de montaña." },
            ],
          },
          {
            id: "dades-actividades-senderismo",
            title: "Senderismo",
            paragraphs: [
              "El entorno del hotel es uno de los mejores lugares de Marruecos para practicar trekking. Existen itinerarios para todos los niveles.",
            ],
            list: [
              { body: "Gargantas del Dades." },
              { body: "Gargantas del Todra." },
              { body: "Valle de las Rosas." },
              { body: "Tizi n'Tagharat." },
              { body: "Aldeas bereberes." },
            ],
          },
          {
            id: "dades-actividades-mulas",
            title: "Excursiones con mulas",
            paragraphs: [
              "Una de las formas más tradicionales de descubrir las montañas del Atlas. Los recorridos permiten acceder a lugares donde únicamente es posible llegar caminando o en mula. Entre ellos destaca el acceso al Refugio Xaluca Ifess Fess.",
            ],
          },
          {
            id: "dades-guia-valle",
            title: "Guía del Valle del Dades",
            paragraphs: [
              "El Valle del Dades constituye uno de los escenarios naturales más espectaculares del país. Modelado durante millones de años por el río Dades, ofrece un paisaje donde se alternan palmerales, montañas, gargantas, kasbahs y pueblos bereberes. Boumalne Dades es la principal puerta de entrada a este territorio.",
            ],
          },
          {
            id: "dades-cerca-ouarzazate",
            title: "Muy cerca de Ouarzazate y Aït Ben Haddou",
            paragraphs: [
              "El hotel se encuentra a aproximadamente 120 kilómetros de Ouarzazate, conocida como la puerta del desierto y uno de los principales centros cinematográficos de Marruecos.",
              "Muy cerca también se encuentra la legendaria Kasbah de Aït Ben Haddou, declarada Patrimonio de la Humanidad por la UNESCO.",
            ],
          },
          {
            id: "dades-gargantas",
            title: "Gargantas del Dades",
            paragraphs: [
              "Impresionantes paredes de roca que alcanzan hasta 500 metros de altura. Uno de los paisajes más fotografiados del Atlas.",
            ],
          },
          {
            id: "dades-dedos-mono",
            title: "Los Dedos de Mono",
            paragraphs: [
              "Curiosas formaciones geológicas creadas por la erosión durante millones de años. Su silueta constituye uno de los iconos naturales del valle.",
            ],
          },
          {
            id: "dades-kasbahs",
            title: "Kasbahs Bereberes",
            paragraphs: [
              "A lo largo del valle pueden visitarse numerosas fortalezas tradicionales.",
            ],
            list: [
              { body: "Kasbah Aït Youl." },
              { body: "Antiguas aldeas fortificadas." },
              { body: "Arquitectura tradicional en adobe." },
            ],
          },
          {
            id: "dades-excursiones-hotel",
            title: "Excursiones organizadas desde el hotel",
            paragraphs: [
              "Desde el hotel pueden organizarse diferentes propuestas para descubrir el valle.",
            ],
            list: [
              { body: "Rutas en 4x4." },
              { body: "Trekking." },
              { body: "Paseos con mulas." },
              { body: "Fotografía de paisaje." },
              { body: "Encuentros culturales." },
            ],
          },
          {
            id: "dades-gargantas-todra",
            title: "Gargantas del Todra",
            paragraphs: [
              "Situadas cerca de Tinghir, constituyen uno de los grandes espectáculos naturales de Marruecos. Sus paredes verticales alcanzan 160 metros de altura, formando un impresionante cañón excavado por el río Todra. Es uno de los mejores lugares del país para:",
            ],
            list: [
              { body: "Senderismo." },
              { body: "Escalada." },
              { body: "Fotografía." },
              { body: "Naturaleza." },
            ],
          },
          {
            id: "dades-boumalne",
            title: "Boumalne Dades",
            paragraphs: [
              "La ciudad constituye el principal centro de servicios del valle. Entre sus principales atractivos destacan:",
            ],
            list: [
              { body: "Mercado tradicional." },
              { body: "Palmerales." },
              { body: "Sistemas de irrigación históricos." },
              { body: "Jardines." },
              { body: "Miradores." },
            ],
          },
          {
            id: "dades-estrellas",
            title: "Observación de estrellas",
            paragraphs: [
              "La escasa contaminación lumínica convierte esta región en uno de los mejores lugares de Marruecos para contemplar el cielo nocturno. Durante muchas noches es posible observar claramente:",
            ],
            list: [
              { body: "La Vía Láctea." },
              { body: "Constelaciones." },
              { body: "Lluvias de estrellas." },
            ],
          },
          {
            id: "dades-festival",
            title: "Festival Bereber",
            paragraphs: [
              "En diferentes épocas del año se celebran festivales donde la música, la danza y las tradiciones amazigh llenan de vida el valle. Es una magnífica oportunidad para conocer de cerca la cultura local.",
            ],
          },
          {
            id: "dades-cierre",
            title: "Un hotel para descubrir el Alto Atlas",
            paragraphs: [
              "Hotel Xaluca Dades representa mucho más que un alojamiento. Es la puerta de entrada a uno de los paisajes más espectaculares de Marruecos.",
              "Aquí se unen naturaleza, cultura, aventura y hospitalidad para ofrecer una experiencia completamente diferente al desierto, permitiendo descubrir otra de las grandes riquezas del país: las montañas del Alto Atlas y el mundo bereber.",
            ],
          },
        ],
      },
      {
        id: "hotel-riad-xaluca",
        title: "Hotel Riad Xaluca",
        subtitle: "Un oasis boutique frente a las dunas del Erg Chebbi",
        paragraphs: [
          "Hay lugares donde el lujo no se mide únicamente por el diseño o el confort, sino por la emoción que produce despertar frente a uno de los paisajes más extraordinarios del planeta. El Hotel Riad Xaluca representa esa filosofía.",
          "Situado frente a las majestuosas dunas del Erg Chebbi, este exclusivo boutique hotel ha sido concebido para quienes desean vivir el Sahara desde una perspectiva más íntima, elegante y personalizada.",
          "Su arquitectura combina el estilo tradicional de los riads marroquíes con un diseño contemporáneo inspirado en la cultura bereber, creando un espacio donde cada detalle transmite autenticidad y tranquilidad.",
          "Aquí, cada amanecer ilumina lentamente las dunas mientras el silencio del desierto envuelve al viajero en una experiencia difícil de describir.",
          "Más que un hotel, el Riad Xaluca es un refugio pensado para disfrutar del Sahara con calma, privacidad y el máximo confort.",
        ],
        subsections: [
          {
            id: "riad-bienvenida",
            title: "Bienvenido al universo Xaluca",
            paragraphs: [
              "Nada más cruzar la entrada del hotel, el visitante descubre un gran espacio diáfano que conecta de forma natural todas las zonas comunes. Todo el conjunto ha sido diseñado utilizando materiales naturales procedentes del sur de Marruecos.",
              "Uno de los elementos más característicos es una pared decorada con una colección de radios vintage, que aporta personalidad y un aire nostálgico al conjunto. Cada rincón del hotel refleja la esencia nómada del Sahara reinterpretada con un estilo contemporáneo.",
            ],
            list: [
              { name: "Zonas comunes", body: "Restaurante · Comedor · Bar · Zona de descanso · Espacios de lectura." },
              { name: "Materiales", body: "Maderas nobles · Piedra · Tejidos bereberes · Cerámica artesanal · Tonos tierra inspirados en el desierto." },
            ],
          },
          {
            id: "riad-diseno",
            title: "Un hotel pensado para disfrutar del desierto",
            paragraphs: [
              "El Riad Xaluca ha sido diseñado para ofrecer una experiencia pausada. No es un gran resort. Es un hotel boutique donde cada espacio invita a disfrutar del paisaje y del silencio.",
              "Su reducido número de habitaciones permite ofrecer un servicio muy personalizado y un ambiente tranquilo durante toda la estancia.",
            ],
          },
          {
            id: "riad-habitaciones",
            title: "Habitaciones",
            paragraphs: [
              "El hotel dispone de 17 habitaciones, distribuidas en diferentes categorías para adaptarse a las necesidades de cada viajero. Las grandes ventanas permiten disfrutar de abundante luz natural y de espectaculares vistas sobre el Erg Chebbi.",
            ],
            list: [
              { name: "Habitaciones Twin", body: "9." },
              { name: "Habitaciones Dobles", body: "2." },
              { name: "Habitaciones Familiares", body: "2." },
              { name: "Junior Suites", body: "2." },
              { name: "Senior Suites", body: "2." },
            ],
          },
          {
            id: "riad-doble-twin",
            title: "Habitaciones Dobles y Twin",
            paragraphs: [
              "Ideales para parejas o viajeros individuales. Su decoración apuesta por líneas sencillas que resaltan la belleza del entorno.",
            ],
            list: [
              { body: "Baño completo." },
              { body: "Aire acondicionado." },
              { body: "Calefacción." },
              { body: "Internet." },
              { body: "Televisión." },
              { body: "Minibar." },
            ],
          },
          {
            id: "riad-familiares",
            title: "Habitaciones Familiares",
            paragraphs: [
              "Pensadas para quienes viajan en familia. Ofrecen mayor amplitud y comodidad, manteniendo el mismo estilo arquitectónico del hotel.",
            ],
          },
          {
            id: "riad-junior",
            title: "Junior Suites",
            paragraphs: [
              "Las Junior Suites incorporan un salón que aporta un espacio adicional para el descanso. La decoración combina tejidos tradicionales, mobiliario artesanal y materiales naturales.",
            ],
          },
          {
            id: "riad-senior",
            title: "Senior Suites",
            paragraphs: [
              "Las Senior Suites representan la categoría superior del hotel. Una opción ideal para quienes buscan una estancia más exclusiva.",
            ],
            list: [
              { body: "Amplio dormitorio." },
              { body: "Salón independiente." },
              { body: "Grandes ventanales." },
              { body: "Baño completo." },
              { body: "Vistas privilegiadas sobre las dunas." },
            ],
          },
          {
            id: "riad-restaurante",
            title: "Restaurante",
            paragraphs: [
              "El restaurante constituye uno de los espacios más emblemáticos del hotel. Desde su interior pueden contemplarse las dunas del Erg Chebbi mientras se disfruta de una propuesta gastronómica basada en la cocina marroquí y mediterránea.",
              "Cada comida se convierte en una experiencia donde el paisaje forma parte del propio restaurante.",
            ],
            list: [
              { body: "Gran mesa central de madera maciza para grupos y celebraciones." },
              { body: "Ambiente íntimo." },
              { body: "Cocina tradicional reinterpretada con un enfoque contemporáneo." },
              { body: "Vistas panorámicas al desierto." },
            ],
          },
          {
            id: "riad-bar",
            title: "Bar",
            paragraphs: [
              "La barra del bar, realizada completamente en madera artesanal, mantiene el estilo tradicional de las construcciones del sur de Marruecos. Es el lugar perfecto para disfrutar de:",
            ],
            list: [
              { body: "Té a la menta." },
              { body: "Café marroquí." },
              { body: "Cócteles." },
              { body: "Refrescos." },
              { body: "Conversaciones tranquilas al finalizar el día." },
            ],
          },
          {
            id: "riad-terraza",
            title: "Terraza Panorámica",
            paragraphs: [
              "Uno de los espacios más espectaculares del hotel. La terraza ofrece vistas abiertas sobre las dunas del Erg Chebbi. Un espacio pensado para contemplar el amanecer, descansar durante la tarde o disfrutar del cielo estrellado por la noche.",
            ],
            list: [
              { body: "Tumbonas King Size." },
              { body: "Sombrillas." },
              { body: "Zona lounge." },
              { body: "Mesas exteriores." },
            ],
          },
          {
            id: "riad-piscina-infinity",
            title: "Piscina Infinity",
            paragraphs: [
              "La piscina infinity constituye la auténtica joya del hotel. Desde el agua es posible contemplar el desierto sin ningún obstáculo visual, creando la sensación de que la piscina se funde con las dunas. Todo ello orientado hacia el Erg Chebbi.",
            ],
            list: [
              { body: "Jacuzzi interior." },
              { body: "Espacios de relax." },
              { body: "Servicio de bebidas." },
              { body: "Tumbonas." },
            ],
          },
          {
            id: "riad-spa",
            title: "Spa & Bienestar",
            paragraphs: [
              "El hotel dispone de una pequeña pero exclusiva zona wellness. Los tratamientos están orientados a proporcionar descanso y recuperación después de las actividades realizadas en el desierto.",
            ],
            list: [
              { body: "Sauna." },
              { body: "Hammam." },
              { body: "Cuatro salas de masaje." },
            ],
          },
          {
            id: "riad-actividades",
            title: "Un hotel para descubrir el Sahara",
            paragraphs: [
              "El Hotel Riad Xaluca no solo invita a descansar. También constituye un magnífico punto de partida para descubrir el entorno. Todas ellas acompañadas por guías locales con un profundo conocimiento del territorio.",
            ],
            list: [
              { body: "Paseos en dromedario." },
              { body: "Excursiones en 4x4." },
              { body: "Rutas en quad." },
              { body: "Visitas a familias nómadas." },
              { body: "Experiencias gastronómicas." },
              { body: "Campamentos en el desierto." },
              { body: "Observación de estrellas." },
            ],
          },
          {
            id: "riad-amanecer-atardecer",
            title: "Despertar frente al Erg Chebbi",
            paragraphs: [
              "Uno de los momentos más especiales de la estancia llega al amanecer. Desde las habitaciones, la terraza o la piscina infinity, los primeros rayos del sol iluminan lentamente las dunas, transformando el paisaje en un inmenso mar de tonos dorados y ocres.",
              "Al atardecer sucede exactamente lo contrario. El sol desaparece lentamente tras el horizonte mientras el silencio del desierto envuelve por completo el hotel.",
              "Son dos momentos que convierten la estancia en una experiencia difícil de olvidar.",
            ],
          },
          {
            id: "riad-cierre",
            title: "Un oasis de tranquilidad",
            paragraphs: [
              "El Hotel Riad Xaluca representa una forma diferente de vivir el Sahara. Más íntimo que un gran hotel y más exclusivo que un campamento, ofrece el equilibrio perfecto entre naturaleza, diseño, hospitalidad y confort.",
              "Cada detalle ha sido cuidadosamente pensado para que el viajero pueda desconectar del ritmo cotidiano y descubrir la magia del desierto desde un entorno elegante, sereno y profundamente auténtico.",
              "Aquí, el lujo consiste en contemplar el horizonte, escuchar el silencio y sentirse parte de uno de los paisajes más extraordinarios de Marruecos.",
            ],
          },
        ],
      },
      {
        id: "auberge-bivouac-la-belle-etoile",
        title: "Auberge Bivouac La Belle Étoile",
        subtitle: "Mil y una noches en el corazón del Erg Chebbi · Merzouga",
        paragraphs: [
          "Dormir en el desierto es una experiencia que va mucho más allá del alojamiento. Es contemplar un cielo repleto de estrellas sin contaminación lumínica, despertar con los primeros rayos de sol iluminando las dunas y descubrir una forma de vida que ha permanecido prácticamente inalterada durante siglos.",
          "El Auberge Bivouac La Belle Étoile representa la esencia más auténtica del Sahara marroquí.",
          "Situado frente a las impresionantes dunas del Erg Chebbi, combina la comodidad de un albergue tradicional con la experiencia única de dormir en un auténtico campamento bereber.",
          "Aquí cada huésped puede elegir entre descansar en el auberge o vivir la experiencia completa alojándose en las tradicionales haimas situadas en pleno desierto.",
          "Abdelkader Hamdaoui · Director.",
        ],
        subsections: [
          {
            id: "belle-etoile-mundo",
            title: "Un mundo de sensaciones entre las dunas",
            paragraphs: [
              "La Belle Étoile ha evolucionado a lo largo de los años adaptándose a las necesidades del viajero actual sin perder nunca su esencia.",
              "Lo que comenzó siendo un pequeño albergue tradicional es hoy uno de los complejos turísticos más completos del Erg Chebbi.",
              "Su filosofía continúa siendo la misma: acercar al viajero a la auténtica cultura nómada del Sahara.",
            ],
          },
          {
            id: "belle-etoile-dos-maneras",
            title: "Dos maneras de vivir el desierto",
            paragraphs: [
              "La Belle Étoile ofrece dos tipos de alojamiento completamente diferentes. Ambas opciones permiten disfrutar del mismo entorno privilegiado.",
            ],
            list: [
              { name: "El Auberge", body: "Ideal para quienes desean disfrutar del desierto manteniendo todas las comodidades de un hotel." },
              { name: "El Campamento de Haimas", body: "Pensado para quienes quieren vivir una experiencia completamente inmersiva durmiendo bajo las estrellas." },
            ],
          },
          {
            id: "belle-etoile-auberge",
            title: "El Auberge",
            paragraphs: [
              "El edificio principal mantiene la arquitectura tradicional de la región. Su decoración apuesta por materiales sencillos y naturales. Todo ello integrado perfectamente con el paisaje.",
            ],
            list: [
              { body: "Piedra." },
              { body: "Adobe." },
              { body: "Madera." },
              { body: "Tejidos bereberes." },
            ],
          },
          {
            id: "belle-etoile-habitaciones",
            title: "Habitaciones del Auberge",
            paragraphs: [
              "El Auberge dispone de 25 habitaciones distribuidas en 23 Standard y 2 Suites. Un alojamiento sencillo pero muy confortable.",
            ],
            list: [
              { name: "Habitaciones Standard", body: "23." },
              { name: "Suites", body: "2." },
              { name: "Equipamiento", body: "Baño privado · Aire acondicionado · Calefacción · Decoración tradicional." },
            ],
          },
          {
            id: "belle-etoile-campamento-haimas",
            title: "El Campamento de Haimas",
            paragraphs: [
              "Frente al Auberge se encuentra el espectacular campamento del Erg Chebbi. Aquí comienza la auténtica experiencia del Sahara.",
              "Dormir entre las dunas permite descubrir el desierto desde una perspectiva completamente diferente. Durante la noche únicamente se escucha el viento desplazando lentamente la arena.",
            ],
          },
          {
            id: "belle-etoile-distribucion",
            title: "Distribución del Campamento",
            paragraphs: [
              "El complejo dispone de un total de 92 haimas, organizadas en diferentes espacios para adaptarse a distintos tipos de viajeros.",
            ],
          },
          {
            id: "belle-etoile-campamento-principal",
            title: "Campamento Principal",
            paragraphs: [
              "El campamento principal está formado por 72 haimas distribuidas en cuatro cuadrantes. Esta distribución permite mantener la comodidad y privacidad incluso cuando el campamento trabaja a plena ocupación.",
            ],
            list: [
              { name: "Bloque 1", body: "Haimas 1 a 18." },
              { name: "Bloque 2", body: "Haimas 19 a 36." },
              { name: "Bloque 3", body: "Haimas 37 a 54." },
              { name: "Bloque 4", body: "Haimas 55 a 72." },
            ],
          },
          {
            id: "belle-etoile-campamento-privado",
            title: "Campamento Privado",
            paragraphs: [
              "Pensado para grupos exclusivos. Dispone de 15 haimas dobles, restaurante privado y zona independiente.",
            ],
            list: [
              { name: "Ideal para", body: "Incentivos · Empresas · Grupos privados · Celebraciones especiales." },
            ],
          },
          {
            id: "belle-etoile-campamento-boutique",
            title: "Campamento Boutique",
            paragraphs: [
              "La opción más exclusiva del complejo. Dispone de 5 haimas, restaurante privado, máxima privacidad y ambiente especialmente tranquilo. Perfecto para parejas o pequeños grupos.",
            ],
          },
          {
            id: "belle-etoile-haimas",
            title: "Las Haimas",
            paragraphs: [
              "Todas las haimas han sido diseñadas para ofrecer el máximo confort sin perder la esencia del Sahara. La experiencia mantiene toda la autenticidad del desierto incorporando las comodidades actuales.",
            ],
            list: [
              { body: "Aire acondicionado." },
              { body: "Calefacción." },
              { body: "Ducha privada." },
              { body: "Lavabo." },
              { body: "Camas confortables." },
              { body: "Iluminación." },
              { body: "Decoración bereber." },
            ],
          },
          {
            id: "belle-etoile-gastronomia",
            title: "Gastronomía",
            paragraphs: [
              "La Belle Étoile ofrece una experiencia gastronómica profundamente ligada a la tradición bereber. El gran comedor principal, construido en forma de haima, sirve cada noche una selección de platos tradicionales.",
            ],
            list: [
              { body: "Tajines." },
              { body: "Cuscús." },
              { body: "Brochetas." },
              { body: "Verduras tradicionales." },
              { body: "Dulces marroquíes." },
              { body: "Té a la menta." },
            ],
          },
          {
            id: "belle-etoile-musica",
            title: "Música Bereber",
            paragraphs: [
              "Después de la cena comienza uno de los momentos más especiales de la jornada. Alrededor de la hoguera los músicos interpretan canciones tradicionales utilizando:",
              "Muchos viajeros terminan participando espontáneamente en la celebración.",
            ],
            list: [
              { body: "Tam-tams." },
              { body: "Instrumentos Gnawa." },
              { body: "Percusión bereber." },
              { body: "Cantos tradicionales." },
            ],
          },
          {
            id: "belle-etoile-amanecer",
            title: "Amanecer en las Dunas",
            paragraphs: [
              "Uno de los grandes privilegios de alojarse en La Belle Étoile consiste en contemplar el amanecer directamente desde el campamento. Los primeros rayos del sol iluminan lentamente las dunas transformando completamente el paisaje. Es uno de los momentos más fotografiados del viaje.",
            ],
          },
          {
            id: "belle-etoile-atardecer",
            title: "Atardecer",
            paragraphs: [
              "Antes de la llegada de la noche el Erg Chebbi ofrece otro espectáculo extraordinario. Las dunas cambian progresivamente de color. Un escenario que parece diferente cada día.",
            ],
            list: [
              { body: "Dorados." },
              { body: "Naranjas." },
              { body: "Rojizos." },
              { body: "Violetas." },
            ],
          },
          {
            id: "belle-etoile-actividades",
            title: "Actividades",
            paragraphs: [
              "Desde La Belle Étoile pueden organizarse numerosas experiencias.",
            ],
          },
          {
            id: "belle-etoile-dromedario",
            title: "Paseos en Dromedario",
            paragraphs: [
              "La actividad más tradicional del Sahara.",
            ],
          },
          {
            id: "belle-etoile-4x4",
            title: "Excursiones en 4x4",
            paragraphs: [
              "Descubriendo:",
            ],
            list: [
              { body: "Palmerales." },
              { body: "Oasis." },
              { body: "Minas." },
              { body: "Aldeas bereberes." },
              { body: "Familias nómadas." },
            ],
          },
          {
            id: "belle-etoile-quad",
            title: "Excursiones en Quad",
            paragraphs: [
              "Para recorrer las dunas con mayor libertad.",
            ],
          },
          {
            id: "belle-etoile-sandboarding",
            title: "Sandboarding",
            paragraphs: [
              "Una de las actividades favoritas para familias y grupos.",
            ],
          },
          {
            id: "belle-etoile-estrellas",
            title: "Observación de Estrellas",
            paragraphs: [
              "La ausencia casi total de contaminación lumínica convierte el Erg Chebbi en uno de los mejores lugares de Marruecos para observar el cielo nocturno.",
            ],
          },
          {
            id: "belle-etoile-capacidades",
            title: "Capacidades · Total del complejo",
            list: [
              { name: "Auberge", body: "25 habitaciones." },
              { name: "Campamento Principal", body: "72 haimas." },
              { name: "Campamento Privado", body: "15 haimas." },
              { name: "Campamento Boutique", body: "5 haimas." },
              { name: "Total haimas", body: "92." },
              { name: "Total habitaciones Auberge", body: "25." },
            ],
          },
          {
            id: "belle-etoile-cierre",
            title: "Una experiencia inolvidable",
            paragraphs: [
              "Auberge Bivouac La Belle Étoile no es simplemente un alojamiento. Es una invitación a descubrir el verdadero Sahara.",
              "Una noche bajo las estrellas. Una cena alrededor del fuego. Un amanecer sobre las dunas. Una conversación compartiendo té con los nómadas. Momentos sencillos que permanecen para siempre en la memoria del viajero.",
              "Desde hace décadas, La Belle Étoile continúa ofreciendo una de las experiencias más auténticas del desierto marroquí, manteniendo intacta la hospitalidad que caracteriza a Grup Xaluca.",
            ],
          },
        ],
      },
      {
        id: "haimas-bivouacs-xaluca",
        title: "Bivouacs Xaluca · Kamkamia Luxury Desert Camp",
        subtitle: "Vive la magia del Sahara bajo las estrellas · Erg Chebbi",
        paragraphs: [
          "Hay experiencias que solo pueden vivirse una vez en la vida. Dormir en el corazón del Sahara, rodeado por un océano infinito de dunas, es una de ellas.",
          "Los Bivouacs Xaluca representan la forma más exclusiva y auténtica de descubrir el desierto del Erg Chebbi, combinando la tradición nómada con el confort contemporáneo.",
          "Inspirados en las antiguas caravanas que durante siglos atravesaron el Sahara, nuestros campamentos permiten disfrutar del desierto con distintos niveles de exclusividad, desde grandes campamentos organizados hasta pequeños campamentos boutique completamente privados.",
          "Aquí, el lujo no se mide únicamente por las instalaciones, sino por la inmensidad del paisaje, el silencio absoluto, la hospitalidad bereber y la emoción de contemplar uno de los cielos estrellados más impresionantes del mundo.",
        ],
        subsections: [
          {
            id: "bivouacs-experiencia",
            title: "Una experiencia inolvidable",
            paragraphs: [
              "La experiencia comienza antes incluso de llegar al campamento. Durante el recorrido, las dunas cambian lentamente de color mientras el sol desciende sobre el horizonte. Al llegar al campamento, el silencio del Sahara envuelve completamente al visitante.",
            ],
            list: [
              { body: "Paseos en dromedario." },
              { body: "Vehículos 4x4." },
              { body: "Excursiones privadas." },
            ],
          },
          {
            id: "bivouacs-bienvenida",
            title: "Bienvenida Bereber",
            paragraphs: [
              "Siguiendo la tradición amazigh, todos los huéspedes son recibidos con la hospitalidad característica del desierto. Desde el primer instante comienza una experiencia profundamente ligada a la cultura del Sahara.",
            ],
            list: [
              { body: "Té tradicional a la menta." },
              { body: "Dulces marroquíes." },
              { body: "Presentación del campamento." },
              { body: "Acompañamiento personalizado hasta la haima." },
            ],
          },
          {
            id: "bivouacs-haimas-general",
            title: "Las Haimas",
            paragraphs: [
              "Las tradicionales tiendas nómadas han sido reinterpretadas para ofrecer el máximo confort sin perder su esencia. Cada una ha sido cuidadosamente diseñada para integrarse plenamente con el paisaje.",
            ],
            list: [
              { body: "Aire acondicionado." },
              { body: "Calefacción." },
              { body: "Baño privado." },
              { body: "Ducha." },
              { body: "Lavabo." },
              { body: "Camas de alta calidad." },
              { body: "Iluminación." },
              { body: "Electricidad." },
              { body: "Decoración artesanal bereber." },
            ],
          },
          {
            id: "bivouacs-gastronomia",
            title: "Gastronomía",
            paragraphs: [
              "Las cenas constituyen uno de los momentos más especiales de la estancia. Los restaurantes de los campamentos ofrecen cocina tradicional marroquí elaborada diariamente con productos frescos. Todo ello servido bajo un cielo completamente estrellado.",
            ],
            list: [
              { body: "Tajines." },
              { body: "Cuscús." },
              { body: "Ensaladas marroquíes." },
              { body: "Brochetas." },
              { body: "Verduras de temporada." },
              { body: "Dulces tradicionales." },
              { body: "Té a la menta." },
            ],
          },
          {
            id: "bivouacs-musica",
            title: "Música Bereber",
            paragraphs: [
              "Al finalizar la cena comienza una auténtica celebración cultural. Alrededor de la hoguera, músicos locales interpretan melodías tradicionales. Muchos viajeros participan espontáneamente en la música y el baile, convirtiendo la noche en una experiencia inolvidable.",
            ],
            list: [
              { body: "Tam-tams." },
              { body: "Percusión bereber." },
              { body: "Instrumentos Gnawa." },
              { body: "Cánticos tradicionales." },
            ],
          },
          {
            id: "bivouacs-amanecer",
            title: "Amanecer sobre el Erg Chebbi",
            paragraphs: [
              "Pocas experiencias igualan la emoción de contemplar el amanecer desde una haima en pleno desierto. Los primeros rayos de sol iluminan lentamente las dunas, transformando el paisaje en un inmenso mar de tonos dorados, anaranjados y rojizos. Es uno de los momentos más especiales de cualquier viaje al Sahara.",
            ],
          },
          {
            id: "bivouacs-kamkamia",
            title: "Campamentos Kamkamia",
            paragraphs: [
              "Los Bivouacs Xaluca disponen de diferentes campamentos independientes adaptados a distintos tipos de viajeros y grupos.",
            ],
          },
          {
            id: "bivouacs-kamkamia-blanc-i",
            title: "Kamkamia Blanc I",
            paragraphs: [
              "El mayor de nuestros campamentos privados. Especialmente recomendado para grandes grupos, viajes de incentivos, circuitos organizados y eventos privados.",
            ],
            list: [
              { name: "Haimas", body: "27." },
              { name: "Restaurante", body: "Privado · Capacidad para 150 comensales." },
            ],
          },
          {
            id: "bivouacs-kamkamia-blanc-ii",
            title: "Kamkamia Blanc II",
            paragraphs: [
              "Un campamento de dimensiones más reducidas que ofrece una experiencia más tranquila e íntima. Ideal para grupos medianos, viajes premium, empresas y celebraciones familiares.",
            ],
            list: [
              { name: "Haimas", body: "24." },
              { name: "Restaurante", body: "Privado · Capacidad para 60 comensales." },
            ],
          },
          {
            id: "bivouacs-kamkamia-blanc-iii",
            title: "Kamkamia Blanc III",
            paragraphs: [
              "El campamento más pequeño de la colección Blanc. Perfecto para pequeños grupos que buscan privacidad absoluta.",
            ],
            list: [
              { name: "Haimas", body: "5." },
              { name: "Restaurante", body: "Sin restaurante propio." },
            ],
          },
          {
            id: "bivouacs-kamkamia-noir",
            title: "Kamkamia Noir",
            paragraphs: [
              "Uno de los mayores campamentos gestionados por Grup Xaluca. Su infraestructura permite alojar importantes circuitos manteniendo un elevado nivel de organización y confort.",
            ],
            list: [
              { name: "Haimas", body: "80." },
              { body: "Gran capacidad operativa." },
              { body: "Preparado para grandes grupos internacionales." },
            ],
          },
          {
            id: "bivouacs-tipologias",
            title: "Tipologías de Haimas",
            paragraphs: [
              "Los campamentos disponen de diferentes configuraciones para adaptarse a las necesidades de cada viajero.",
            ],
          },
          {
            id: "bivouacs-haima-doble",
            title: "Haimas Dobles",
            paragraphs: [
              "Ideales para parejas.",
            ],
            list: [
              { body: "Cama matrimonial." },
              { body: "Baño privado." },
              { body: "Aire acondicionado." },
              { body: "Calefacción." },
            ],
          },
          {
            id: "bivouacs-haima-twin",
            title: "Haimas Twin",
            paragraphs: [
              "Especialmente utilizadas por grupos y circuitos organizados.",
            ],
            list: [
              { body: "Dos camas individuales." },
              { body: "Baño privado." },
              { body: "Aire acondicionado." },
              { body: "Calefacción." },
            ],
          },
          {
            id: "bivouacs-campamentos-privados",
            title: "Campamentos Privados",
            paragraphs: [
              "Además de los campamentos principales, Grup Xaluca dispone de espacios completamente privados para eventos exclusivos. Cada campamento dispone de comedor propio y espacios completamente independientes.",
            ],
            list: [
              { body: "Incentivos." },
              { body: "Bodas." },
              { body: "Celebraciones." },
              { body: "Viajes corporativos." },
              { body: "Grupos privados." },
            ],
          },
          {
            id: "bivouacs-boutique",
            title: "Campamento Boutique",
            paragraphs: [
              "La opción más exclusiva de toda la colección. Ideal para parejas o pequeños grupos que buscan una experiencia premium en pleno desierto.",
            ],
            list: [
              { name: "Haimas", body: "Únicamente 5." },
              { body: "Restaurante privado." },
              { body: "Servicio personalizado." },
              { body: "Máxima privacidad." },
              { body: "Ambiente especialmente tranquilo." },
            ],
          },
          {
            id: "bivouacs-actividades",
            title: "Actividades",
            paragraphs: [
              "Desde los Bivouacs Xaluca pueden organizarse numerosas experiencias.",
            ],
          },
          {
            id: "bivouacs-dromedario",
            title: "Paseos en dromedario",
            paragraphs: [
              "La forma más tradicional de descubrir el Erg Chebbi.",
            ],
          },
          {
            id: "bivouacs-4x4",
            title: "Excursiones en 4x4",
            paragraphs: [
              "Recorriendo:",
            ],
            list: [
              { body: "Palmerales." },
              { body: "Oasis." },
              { body: "Minas de M'Fis." },
              { body: "Khamlia." },
              { body: "Familias nómadas." },
              { body: "Paisajes del Sahara." },
            ],
          },
          {
            id: "bivouacs-quad",
            title: "Excursiones en Quad",
            paragraphs: [
              "Una forma emocionante de recorrer las dunas.",
            ],
          },
          {
            id: "bivouacs-buggy",
            title: "Excursiones en Buggy",
            paragraphs: [
              "Especialmente recomendadas para quienes buscan aventura y diversión.",
            ],
          },
          {
            id: "bivouacs-sandboarding",
            title: "Sandboarding",
            paragraphs: [
              "Descender por las dunas utilizando tablas especiales. Una actividad perfecta para todas las edades.",
            ],
          },
          {
            id: "bivouacs-astronomia",
            title: "Observación Astronómica",
            paragraphs: [
              "El Sahara ofrece uno de los cielos nocturnos más limpios del norte de África. Una experiencia única para los amantes de la naturaleza y la astronomía.",
            ],
            list: [
              { body: "La Vía Láctea." },
              { body: "Constelaciones." },
              { body: "Planetas." },
              { body: "Lluvias de estrellas." },
            ],
          },
          {
            id: "bivouacs-capacidades",
            title: "Capacidades · Resumen de los campamentos Kamkamia",
            list: [
              { name: "Kamkamia Blanc I", body: "27 haimas · Restaurante 150 personas." },
              { name: "Kamkamia Blanc II", body: "24 haimas · Restaurante 60 personas." },
              { name: "Kamkamia Blanc III", body: "5 haimas · Sin restaurante." },
              { name: "Kamkamia Noir", body: "80 haimas." },
            ],
          },
          {
            id: "bivouacs-esencia",
            title: "La esencia del desierto",
            paragraphs: [
              "Dormir en una haima supone recuperar una forma de viajar que apenas ha cambiado desde hace siglos. Aquí no existen prisas. Solo el ritmo pausado del desierto.",
              "El sonido del viento desplazando la arena. El aroma del té recién preparado. La conversación alrededor de la hoguera. Y un cielo infinito cubierto de estrellas.",
              "Los Bivouacs Xaluca – Kamkamia Luxury Desert Camp representan la máxima expresión de la hospitalidad bereber, ofreciendo una experiencia exclusiva donde tradición, naturaleza y confort conviven en perfecta armonía.",
              "Más que una noche en el desierto, es un recuerdo que permanecerá para siempre en la memoria del viajero.",
            ],
          },
        ],
      },
      {
        id: "hotel-xaluca-spa-aguelmame",
        title: "Hotel Xaluca Spa Aguelmame Sidi Ali",
        subtitle: "Una joya escondida entre lagos, bosques y montañas · Atlas Medio",
        paragraphs: [
          "Existen lugares que parecen detenidos en el tiempo. El Hotel Xaluca Spa Aguelmame Sidi Ali es uno de ellos.",
          "Situado a orillas del espectacular lago Aguelmame Sidi Ali, en pleno Atlas Medio, este hotel boutique de alta montaña ofrece una experiencia completamente diferente al resto de establecimientos de Grup Xaluca.",
          "Aquí el desierto deja paso a bosques de cedros, volcanes extinguidos, lagos de alta montaña y un paisaje donde el silencio y la naturaleza son los auténticos protagonistas.",
          "Antiguamente concebido como un refugio de caza y pesca, el edificio ha sido completamente restaurado por Grup Xaluca respetando su esencia original y transformándolo en uno de los hoteles más exclusivos de montaña de Marruecos.",
          "Construido casi íntegramente en madera de cedro y piedra natural, el hotel combina lujo, bienestar y naturaleza en un entorno absolutamente único.",
          "Hassan · Director.",
        ],
        subsections: [
          {
            id: "aguelmame-datos",
            title: "Datos principales",
            paragraphs: [
              "El lago Aguelmame Sidi Ali ocupa aproximadamente 400 hectáreas y alcanza una profundidad cercana a 36 metros, convirtiéndose en uno de los lagos más profundos de Marruecos.",
            ],
            list: [
              { name: "Región", body: "Atlas Medio." },
              { name: "Entorno", body: "Lago Aguelmame Sidi Ali." },
              { name: "Altitud", body: "2.100 metros." },
              { name: "Habitaciones", body: "32." },
              { name: "Distancia", body: "A 140 km del Aeropuerto de Fez (aproximadamente 2 horas)." },
            ],
          },
          {
            id: "aguelmame-historia",
            title: "Una historia de recuperación",
            paragraphs: [
              "El edificio original fue construido como refugio para actividades de caza y pesca. Tras años de abandono, Grup Xaluca emprendió un ambicioso proyecto de restauración en colaboración con numerosos artesanos, carpinteros y ebanistas de la región.",
              "El resultado es un hotel que conserva el alma del edificio original, incorporando todas las comodidades contemporáneas. Cada rincón refleja el trabajo artesanal realizado con materiales locales.",
            ],
          },
          {
            id: "aguelmame-distribucion",
            title: "Distribución de habitaciones",
            paragraphs: [
              "El hotel dispone de 32 habitaciones, distribuidas en diferentes categorías según su orientación y nivel de confort.",
            ],
            list: [
              { name: "Twin View Lake", body: "1 habitación." },
              { name: "Twin View Volcano", body: "8 habitaciones." },
              { name: "Double View Lake", body: "7 habitaciones." },
              { name: "Cuádruple", body: "1 habitación." },
              { name: "Junior Suite View Lake", body: "6 habitaciones." },
              { name: "Junior Suite View Volcano", body: "6 habitaciones." },
              { name: "Senior Suite View Lake", body: "1 habitación." },
              { name: "Senior Suite View Volcano", body: "1 habitación." },
              { name: "Royal Suite", body: "1 habitación independiente." },
            ],
          },
          {
            id: "aguelmame-standard",
            title: "Habitaciones Standard",
            paragraphs: [
              "Las habitaciones Standard ofrecen vistas al lago o al volcán. Construidas íntegramente en madera, destacan por su ambiente cálido y acogedor.",
            ],
            list: [
              { body: "Baño completo." },
              { body: "Televisión vía satélite." },
              { body: "Internet." },
              { body: "Minibar." },
              { body: "Teléfono." },
              { body: "Calefacción." },
              { body: "Decoración bereber." },
            ],
          },
          {
            id: "aguelmame-junior",
            title: "Junior Suites",
            paragraphs: [
              "Las Junior Suites incorporan un salón privado que permite disfrutar de una estancia mucho más amplia. Se mantienen los mismos materiales nobles utilizados en todo el hotel:",
            ],
            list: [
              { body: "Cedro." },
              { body: "Piedra." },
              { body: "Tejidos bereberes." },
              { body: "Artesanía local." },
            ],
          },
          {
            id: "aguelmame-senior",
            title: "Senior Suites",
            paragraphs: [
              "Las dos Senior Suites representan una categoría superior. Una de ellas ofrece vistas al lago y la otra al volcán.",
            ],
            list: [
              { body: "Dormitorio." },
              { body: "Salón." },
              { body: "Jacuzzi." },
              { body: "Amplias vistas panorámicas." },
              { body: "Baño completo." },
            ],
          },
          {
            id: "aguelmame-royal",
            title: "Royal Suite",
            paragraphs: [
              "La Royal Suite constituye el alojamiento más exclusivo del hotel. Situada en un edificio independiente, ofrece total privacidad. Una experiencia diseñada para quienes buscan máxima tranquilidad.",
            ],
            list: [
              { body: "Dormitorio principal." },
              { body: "Chimenea." },
              { body: "Jacuzzi." },
              { body: "Terraza privada." },
              { body: "Vistas privilegiadas al lago." },
              { body: "Baño completo." },
              { body: "Televisión." },
              { body: "Minibar." },
            ],
          },
          {
            id: "aguelmame-spa",
            title: "Spa & Wellness — Bienestar en plena naturaleza",
            paragraphs: [
              "El Spa constituye uno de los grandes atractivos del hotel, rodeado por un paisaje de montaña único en Marruecos.",
            ],
            list: [
              { body: "Piscina climatizada." },
              { body: "Hidromasaje." },
              { body: "Sauna." },
              { body: "Hammam." },
              { body: "Dos salas de masaje individuales." },
              { body: "Una sala de masaje para parejas." },
              { body: "Zona de duchas." },
            ],
          },
          {
            id: "aguelmame-tratamientos",
            title: "Tratamientos",
            paragraphs: [
              "Todos ellos realizados utilizando técnicas tradicionales marroquíes.",
            ],
            list: [
              { name: "Individuales", body: "Hammam tradicional · Hammam con exfoliación · Sauna · Masaje relajante · Masaje energético." },
              { name: "Combinados", body: "Hammam + masaje · Sauna + masaje." },
            ],
          },
          {
            id: "aguelmame-restauracion",
            title: "Restauración",
            paragraphs: [
              "El restaurante del hotel ofrece una de las mejores vistas del lago. Su propuesta gastronómica combina cocina marroquí, productos locales y cocina internacional. El comedor se complementa con una acogedora zona de bar presidida por una gran chimenea.",
            ],
          },
          {
            id: "aguelmame-salones",
            title: "Salones",
            paragraphs: [
              "El hotel dispone de varios espacios comunes pensados para el descanso. Durante el invierno estos espacios adquieren un ambiente especialmente acogedor.",
            ],
            list: [
              { body: "Salón principal." },
              { body: "Bar." },
              { body: "Biblioteca." },
              { body: "Zona de billar." },
              { body: "Chimenea." },
              { body: "Grandes sofás." },
              { body: "Vistas panorámicas al lago." },
            ],
          },
          {
            id: "aguelmame-deporte",
            title: "Área deportiva",
            paragraphs: [
              "El hotel dispone de un gimnasio equipado para quienes desean mantenerse activos durante su estancia.",
            ],
          },
          {
            id: "aguelmame-conferencias",
            title: "Sala de Conferencias",
            paragraphs: [
              "Preparada para reuniones, eventos, incentivos, presentaciones y celebraciones privadas.",
            ],
          },
          {
            id: "aguelmame-senderismo",
            title: "Senderismo alrededor del lago",
            paragraphs: [
              "Diversos senderos permiten recorrer el perímetro del Aguelmame Sidi Ali disfrutando de paisajes espectaculares.",
            ],
          },
          {
            id: "aguelmame-trekking",
            title: "Trekking por los bosques de cedro",
            paragraphs: [
              "Los alrededores albergan algunos de los bosques de cedros más importantes de Marruecos. Las rutas permiten descubrir:",
            ],
            list: [
              { body: "Fauna." },
              { body: "Flora." },
              { body: "Volcanes." },
              { body: "Miradores." },
            ],
          },
          {
            id: "aguelmame-banos-frios",
            title: "Baños de agua fría",
            paragraphs: [
              "Uno de los atractivos más singulares consiste en realizar inmersiones en las frías aguas del lago. Muchos visitantes practican esta actividad por sus posibles beneficios relacionados con:",
            ],
            list: [
              { body: "Recuperación física." },
              { body: "Circulación." },
              { body: "Bienestar." },
            ],
          },
          {
            id: "aguelmame-recepcion",
            title: "Una recepción con historia",
            paragraphs: [
              "Nada más entrar, el visitante descubre una recepción construida íntegramente en madera. Uno de sus elementos más especiales es el antiguo cartel original del refugio de caza y pesca, conservado como homenaje a la historia del edificio. Todo ello crea una atmósfera cálida desde el primer momento.",
            ],
            list: [
              { body: "Una gran mesa realizada con un tronco natural." },
              { body: "Esculturas de madera talladas a mano." },
              { body: "Teléfonos vintage." },
              { body: "Espejos artesanales." },
              { body: "Decoración inspirada en la montaña." },
            ],
          },
          {
            id: "aguelmame-bar",
            title: "El Bar",
            paragraphs: [
              "El bar constituye uno de los espacios más acogedores del hotel. El ambiente recuerda al de los grandes refugios alpinos, combinado con elementos tradicionales marroquíes.",
            ],
            list: [
              { body: "Sofás Chester." },
              { body: "Chimenea de cobre y madera." },
              { body: "Televisión." },
              { body: "Barra." },
              { body: "Amplias zonas de descanso." },
            ],
          },
          {
            id: "aguelmame-restaurante-guia",
            title: "Restaurante",
            paragraphs: [
              "Su gastronomía combina cocina tradicional marroquí con propuestas contemporáneas.",
            ],
            list: [
              { body: "Grandes dimensiones." },
              { body: "Chimenea central." },
              { body: "Zona buffet." },
              { body: "Mesas de madera maciza." },
              { body: "Vistas panorámicas al lago." },
            ],
          },
          {
            id: "aguelmame-terraza",
            title: "Terraza panorámica",
            paragraphs: [
              "Desde la terraza exterior pueden contemplarse algunas de las mejores vistas del lago. Durante el invierno el paisaje completamente nevado transforma el entorno en uno de los escenarios más sorprendentes de Marruecos. Es uno de los pocos hoteles del país donde es posible disfrutar simultáneamente de:",
            ],
            list: [
              { body: "Lago." },
              { body: "Bosques." },
              { body: "Montañas." },
              { body: "Nieve." },
            ],
          },
          {
            id: "aguelmame-habitaciones-guia",
            title: "Las habitaciones",
            paragraphs: [
              "Todas las habitaciones mantienen una misma filosofía: naturaleza, madera y calidez. Cada estancia invita a desconectar completamente del ritmo cotidiano.",
            ],
            list: [
              { body: "Mobiliario artesanal." },
              { body: "Alfombras bereberes." },
              { body: "Grandes ventanales." },
              { body: "Cafetera." },
              { body: "Tetera." },
              { body: "Minibar." },
              { body: "Baño completo." },
            ],
          },
          {
            id: "aguelmame-estudio-billar",
            title: "Zona de estudio y billar",
            paragraphs: [
              "En la planta superior se encuentra uno de los espacios más tranquilos del hotel. Un lugar pensado para disfrutar del paisaje con absoluta tranquilidad.",
            ],
            list: [
              { body: "Biblioteca." },
              { body: "Zona de lectura." },
              { body: "Billar." },
              { body: "Chimenea." },
              { body: "Grandes ventanales." },
            ],
          },
          {
            id: "aguelmame-jacuzzi",
            title: "Jacuzzi exterior",
            paragraphs: [
              "Uno de los rincones más espectaculares del hotel. Situado sobre una pequeña colina junto al edificio principal, el jacuzzi exterior ofrece una vista panorámica incomparable sobre el lago Aguelmame Sidi Ali. Desde aquí pueden contemplarse amaneceres y atardeceres verdaderamente inolvidables.",
            ],
          },
          {
            id: "aguelmame-cierre",
            title: "Un refugio diferente",
            paragraphs: [
              "El Hotel Xaluca Spa Aguelmame Sidi Ali representa otra forma de descubrir Marruecos. Lejos del bullicio de las ciudades y del desierto, este establecimiento invita a reconectar con la naturaleza, disfrutar del silencio, respirar aire puro y vivir una experiencia de bienestar rodeado por uno de los paisajes de montaña más espectaculares del país.",
              "Su combinación de arquitectura, spa, gastronomía, naturaleza y hospitalidad convierte este hotel en uno de los proyectos más singulares de Grup Xaluca.",
            ],
          },
        ],
      },
      {
        id: "xaluca-ifess-fess-refuge",
        title: "Xaluca Ifess Fess Refuge de Montagne",
        subtitle: "Un refugio suspendido entre montañas y cielos infinitos · Macizo del M'Goun",
        paragraphs: [
          "Existen lugares que solo pueden descubrirse caminando. Lugares donde el silencio únicamente es interrumpido por el viento, el sonido de un río o el paso tranquilo de una mula entre senderos centenarios.",
          "Xaluca Ifess Fess Refuge de Montagne es uno de esos lugares. Situado en pleno macizo del M'Goun, en el corazón del Alto Atlas marroquí, este refugio representa la experiencia de montaña más auténtica de Grup Xaluca.",
          "Aquí no llegan las carreteras. No existen grandes edificios. No hay ruido. Solo naturaleza, montañas, cultura amazigh y una hospitalidad que permanece intacta desde hace generaciones.",
        ],
        subsections: [
          {
            id: "ifess-acceso",
            title: "Un refugio accesible únicamente a pie",
            paragraphs: [
              "Llegar al refugio forma parte de la experiencia. El acceso únicamente puede realizarse a pie o en mula. Cada paso acerca al viajero a un Marruecos completamente diferente.",
            ],
            list: [
              { body: "Ríos." },
              { body: "Bosques." },
              { body: "Gargantas." },
              { body: "Pequeñas aldeas amazigh." },
              { body: "Senderos tradicionales." },
            ],
          },
          {
            id: "ifess-mgoun",
            title: "El Valle del M'Goun",
            paragraphs: [
              "El refugio se encuentra en una de las zonas de montaña más espectaculares del país. El macizo del M'Goun, con más de 4.000 metros de altitud, constituye uno de los grandes destinos para el senderismo y el trekking en Marruecos. Su paisaje alterna:",
            ],
            list: [
              { body: "Altas montañas." },
              { body: "Praderas alpinas." },
              { body: "Barrancos." },
              { body: "Bosques." },
              { body: "Ríos cristalinos." },
              { body: "Pueblos tradicionales." },
            ],
          },
          {
            id: "ifess-experiencia",
            title: "Una experiencia auténtica",
            paragraphs: [
              "El refugio mantiene una filosofía muy clara: ofrecer únicamente lo necesario para disfrutar plenamente del entorno. Aquí el verdadero lujo es:",
            ],
            list: [
              { body: "El silencio." },
              { body: "El cielo estrellado." },
              { body: "El contacto con la naturaleza." },
              { body: "La convivencia con la cultura amazigh." },
            ],
          },
          {
            id: "ifess-habitaciones",
            title: "Habitaciones",
            paragraphs: [
              "Las habitaciones son sencillas, acogedoras y funcionales. Construidas con materiales tradicionales de la región, permiten descansar plenamente después de una jornada de montaña. Todo ello respetando el entorno natural.",
            ],
            list: [
              { body: "Piedra." },
              { body: "Madera." },
              { body: "Tejidos bereberes." },
              { body: "Decoración artesanal." },
            ],
          },
          {
            id: "ifess-gastronomia",
            title: "Gastronomía",
            paragraphs: [
              "La cocina del refugio recupera recetas tradicionales transmitidas durante generaciones. Una cocina sencilla, auténtica y profundamente ligada al territorio.",
            ],
            list: [
              { body: "Tajines." },
              { body: "Sopas bereberes." },
              { body: "Pan artesanal." },
              { body: "Cuscús." },
              { body: "Verduras de temporada." },
              { body: "Té a la menta." },
            ],
          },
          {
            id: "ifess-trekking",
            title: "Trekking",
            paragraphs: [
              "Se organizan rutas de diferentes niveles, desde pequeños recorridos hasta travesías de varios días. Entre las más populares destacan:",
            ],
            list: [
              { body: "Ascensión al M'Goun." },
              { body: "Gargantas del M'Goun." },
              { body: "Aldeas bereberes." },
              { body: "Pastos de montaña." },
            ],
          },
          {
            id: "ifess-senderismo",
            title: "Senderismo",
            paragraphs: [
              "Numerosos senderos permiten descubrir el entorno natural. Cada estación del año transforma completamente el paisaje.",
            ],
            list: [
              { body: "Bosques." },
              { body: "Ríos." },
              { body: "Cascadas." },
              { body: "Valles." },
              { body: "Puentes tradicionales." },
            ],
          },
          {
            id: "ifess-mulas",
            title: "Rutas con mulas",
            paragraphs: [
              "Las mulas siguen siendo el principal medio de transporte en muchas zonas del Atlas. Los viajeros pueden recorrer antiguos caminos utilizados durante siglos por las comunidades amazigh.",
            ],
          },
          {
            id: "ifess-fotografia",
            title: "Fotografía de naturaleza",
            paragraphs: [
              "El entorno ofrece infinitas posibilidades para la fotografía. Especialmente durante:",
            ],
            list: [
              { body: "Amanecer." },
              { body: "Atardecer." },
              { body: "Primavera." },
              { body: "Otoño." },
            ],
          },
          {
            id: "ifess-cultura",
            title: "Cultura Amazigh",
            paragraphs: [
              "Durante las rutas es posible conocer pequeñas aldeas donde todavía se conservan formas de vida tradicionales. Los viajeros pueden descubrir:",
            ],
            list: [
              { body: "Arquitectura tradicional." },
              { body: "Agricultura de montaña." },
              { body: "Artesanía." },
              { body: "Costumbres locales." },
              { body: "Hospitalidad amazigh." },
            ],
          },
          {
            id: "ifess-cierre",
            title: "Un lugar para desconectar",
            paragraphs: [
              "Xaluca Ifess Fess no pretende ser un hotel de lujo. Pretende ofrecer algo mucho más difícil de encontrar: tiempo, silencio, naturaleza y montaña.",
              "Es el lugar perfecto para quienes desean alejarse del turismo convencional y vivir una experiencia auténtica en el corazón del Atlas.",
            ],
          },
        ],
      },
      {
        id: "xaluca-ait-ben-haddou",
        title: "Xaluca Aït Ben Haddou Maison d'Hôte",
        subtitle: "Proyecto especial · Patrimonio UNESCO",
        paragraphs: [
          "Un proyecto especial dentro de la Kasbah Aït Ben Haddou, Patrimonio de la Humanidad por la UNESCO.",
        ],
      },
    ],
  },

  {
    id: "experiencias",
    label: "Experiencias",
    icon: "Compass",
    sections: [
      {
        id: "experiencias-mucho-mas",
        title: "Mucho más que alojamientos",
        paragraphs: [
          "Grup Xaluca no se limita a ofrecer hoteles. Nuestro objetivo es que cada viajero descubra el verdadero sur de Marruecos mediante experiencias cuidadosamente diseñadas.",
          "Cada actividad está organizada por equipos locales con un profundo conocimiento del territorio.",
        ],
      },
      {
        id: "experiencias-desierto",
        title: "Experiencias en el Desierto",
        subtitle: "Erg Chebbi · Merzouga · Sáhara",
        subsections: [
          {
            id: "exp-desierto-4x4",
            title: "Excursiones en 4x4",
            paragraphs: [
              "Una de las formas más completas de descubrir el Sahara. Las rutas permiten visitar:",
            ],
            list: [
              { body: "Erg Chebbi." },
              { body: "Palmerales." },
              { body: "Minas." },
              { body: "Aldeas bereberes." },
              { body: "Familias nómadas." },
              { body: "Oasis escondidos." },
            ],
          },
          {
            id: "exp-desierto-dromedario",
            title: "Paseos en dromedario",
            paragraphs: [
              "La experiencia más emblemática del desierto. Las excursiones permiten recorrer lentamente las dunas siguiendo las antiguas rutas caravaneras. Especialmente recomendables al amanecer y al atardecer.",
            ],
          },
          {
            id: "exp-desierto-campamentos",
            title: "Noches en campamentos",
            paragraphs: [
              "Dormir en pleno Sahara constituye una de las experiencias más memorables del viaje. Incluye:",
            ],
            list: [
              { body: "Cena tradicional." },
              { body: "Música bereber." },
              { body: "Hoguera." },
              { body: "Observación de estrellas." },
            ],
          },
          {
            id: "exp-desierto-quads",
            title: "Quads",
            paragraphs: [
              "Para quienes buscan mayor adrenalina. Rutas guiadas por dunas, hamadas y palmerales.",
            ],
          },
          {
            id: "exp-desierto-buggies",
            title: "Buggies",
            paragraphs: [
              "Una alternativa deportiva para descubrir el desierto desde otra perspectiva.",
            ],
          },
          {
            id: "exp-desierto-sandboarding",
            title: "Sandboarding",
            paragraphs: [
              "Deslizarse sobre las dunas del Erg Chebbi se ha convertido en una de las actividades favoritas para familias y grupos.",
            ],
          },
        ],
      },
      {
        id: "experiencias-atlas",
        title: "Experiencias en el Atlas",
        subtitle: "Alto Atlas · Atlas Medio · Cultura Amazigh",
        subsections: [
          {
            id: "exp-atlas-trekking",
            title: "Trekking",
            paragraphs: [
              "Rutas adaptadas a todos los niveles. Permiten descubrir:",
            ],
            list: [
              { body: "Gargantas." },
              { body: "Bosques." },
              { body: "Valles." },
              { body: "Pueblos amazigh." },
            ],
          },
          {
            id: "exp-atlas-senderismo",
            title: "Senderismo",
            paragraphs: [
              "Excursiones de media jornada o jornada completa.",
            ],
          },
          {
            id: "exp-atlas-mulas",
            title: "Excursiones con mulas",
            paragraphs: [
              "Una forma tradicional de recorrer la montaña.",
            ],
          },
          {
            id: "exp-atlas-fauna",
            title: "Observación de fauna y flora",
            paragraphs: [
              "Los bosques del Atlas albergan una enorme riqueza natural.",
            ],
          },
        ],
      },
      {
        id: "experiencias-culturales",
        title: "Experiencias Culturales",
        paragraphs: [
          "Grup Xaluca organiza visitas para descubrir el patrimonio cultural del sur de Marruecos. Entre ellas:",
        ],
        list: [
          { body: "Kasbahs." },
          { body: "Ksar tradicionales." },
          { body: "Talleres artesanales." },
          { body: "Mercados locales." },
          { body: "Museos." },
          { body: "Música Gnawa." },
          { body: "Gastronomía bereber." },
        ],
      },
      {
        id: "experiencias-bienestar",
        title: "Bienestar",
        paragraphs: [
          "El bienestar forma parte de la filosofía de Grup Xaluca. Nuestros establecimientos ofrecen diferentes experiencias de relajación:",
        ],
        list: [
          { body: "Spa." },
          { body: "Hammam." },
          { body: "Sauna." },
          { body: "Masajes." },
          { body: "Hidroterapia." },
          { body: "Piscinas climatizadas." },
        ],
      },
      {
        id: "experiencias-gastronomia",
        title: "Gastronomía",
        paragraphs: [
          "La cocina constituye uno de los pilares de la experiencia Xaluca. Nuestros restaurantes recuperan recetas tradicionales elaboradas con productos locales.",
        ],
        list: [
          { name: "Especialidades", body: "Tajines · Cuscús · Pastela · Cocina amazigh · Cocina internacional." },
        ],
      },
      {
        id: "experiencias-eventos",
        title: "Eventos y Congresos",
        paragraphs: [
          "Varios establecimientos del grupo disponen de infraestructuras preparadas para organizar:",
        ],
        list: [
          { body: "Congresos." },
          { body: "Convenciones." },
          { body: "Incentivos." },
          { body: "Reuniones." },
          { body: "Bodas." },
          { body: "Celebraciones privadas." },
          { body: "Eventos corporativos." },
        ],
      },
      {
        id: "experiencias-filosofia",
        title: "La Filosofía Xaluca",
        paragraphs: [
          "Más de tres décadas de experiencia nos han enseñado que un viaje no se recuerda únicamente por el lugar visitado. Se recuerda por las personas que conocimos. Por las conversaciones compartidas. Por los amaneceres contemplados en silencio. Por una cena alrededor del fuego. Por un té ofrecido con hospitalidad. Por la emoción de descubrir un paisaje que permanece para siempre en la memoria.",
          "Esa es la esencia de Grup Xaluca. No construimos únicamente hoteles. Creamos lugares donde cada viajero puede sentirse parte de una historia que comenzó hace más de treinta años y que continúa escribiéndose cada día entre las dunas del Sahara, las montañas del Atlas y la hospitalidad de Marruecos.",
        ],
      },
      {
        id: "cierra-el-circulo",
        title: "Cierra el círculo con Grup Xaluca",
        paragraphs: [
          "Para las agencias de viajes, Grup Xaluca representa la solución integral para descubrir el sur de Marruecos. Nuestra oferta abarca una variada gama de hoteles de diferentes categorías, campamentos exclusivos en el desierto, y servicios turísticos personalizados que garantizan una experiencia inolvidable para cada viajero.",
          "Si buscas un destino donde la tradición se fusiona con la aventura, la comodidad y el lujo, Grup Xaluca es tu mejor elección para explorar la auténtica esencia del sur de Marruecos. Bienvenidos a la magia del desierto. Bienvenidos a Grup Xaluca.",
        ],
      },
    ],
  },

  {
    id: "compania",
    label: "La Compañía",
    icon: "Building2",
    sections: [
      {
        id: "historia-filosofia",
        title: "Nuestra Historia y Filosofía",
        paragraphs: [
          "Con más de 30 años de trayectoria en el sector hotelero y turístico de Marruecos, somos una empresa familiar comprometida con la excelencia y la autenticidad.",
          "Grup Xaluca está compuesto por un equipo humano altamente cualificado, distribuido en diversos establecimientos hoteleros, albergues y bivouacs situados en la región de Mequinez-Tafilalet. Además, contamos con una empresa de transporte turístico en Marruecos y una agencia de viajes mayorista y minorista en España, lo que nos permite ofrecer un servicio completo y coordinado.",
        ],
      },
      {
        id: "presencia-sur-marruecos",
        title: "Nuestra Presencia en el Sur de Marruecos",
        paragraphs: [
          "Nuestros establecimientos estratégicamente ubicados dan alojamiento a más de 100.000 viajeros de diversas nacionalidades cada año, brindando experiencias únicas en entornos espectaculares:",
        ],
        list: [
          {
            name: "Kasbah Hotel Xaluca Arfoud",
            body: "Un referente en el sur de Marruecos, con 200 habitaciones que combinan confort y un diseño que refleja la esencia marroquí.",
          },
          {
            name: "Kasbah Hotel Tombouctou (Merzouga)",
            body: "Nuestro primer establecimiento, emblemático por su hospitalidad y su espectacular ubicación frente a las dunas del Erg Chebbi.",
          },
          {
            name: "Hotel Xaluca Dades (Boulmane Dades)",
            body: "Un hotel que fusiona la arquitectura africana con el encanto del paisaje montañoso del Medio Atlas.",
          },
          {
            name: "Hotel Xaluca Spa Aguelmame Sidi Ali",
            body: "Un oasis de bienestar en el corazón de la naturaleza marroquí.",
          },
          {
            name: "Boutique Hotel Riad Xaluca y Xaluca Ifess Fess Refuge de Montagne",
            body: "Opciones ideales para quienes buscan experiencias más íntimas o de aventura en las dunas del Erg Chebbi o en la cordillera del Atlas.",
          },
          {
            name: "Auberge La Belle Étoile (Merzouga)",
            body: "Para vivir la auténtica experiencia del desierto, durmiendo bajo un cielo estrellado.",
          },
          {
            name: "Bivouacs Xaluca",
            body: "Campamentos exclusivos que permiten disfrutar del desierto con todas las comodidades.",
          },
          {
            name: "Xaluca Aït Ben Haddou Maison d'Hôte",
            body: "Un proyecto especial dentro de la Kasbah Aït Ben Haddou, Patrimonio de la Humanidad por la UNESCO.",
          },
        ],
      },
      {
        id: "liderazgo-vision",
        title: "Liderazgo y Visión",
        paragraphs: [
          "Al frente de Grup Xaluca, Tayeb Ettaiek (Marruecos) y Lluís Pont (España) han sabido integrar tradición y modernidad en cada uno de nuestros proyectos, creando espacios que respetan la cultura local y ofrecen el máximo confort.",
          "La logística en Marruecos está coordinada por Lakhlafa Ettaiek, experto en las necesidades de los viajeros, mientras que Sadoq Abdedaim desempeña el papel de director general del grupo, asegurando la excelencia operativa de todos nuestros servicios.",
        ],
      },
      {
        id: "linea-tiempo",
        title: "Grup Xaluca a lo Largo del Tiempo",
        paragraphs: [
          "Desde la inauguración de nuestro primer hotel en 1998 hasta los proyectos en desarrollo en la actualidad, hemos crecido manteniendo la pasión por ofrecer experiencias auténticas y memorables. Con cada nuevo desafío, reafirmamos nuestro compromiso de ser el puente entre el viajero y la magia del sur de Marruecos.",
        ],
      },
    ],
  },

  {
    id: "servicios",
    label: "Servicios Complementarios",
    icon: "Briefcase",
    sections: [
      {
        id: "servicios-complementarios",
        title: "Servicios Complementarios",
        list: [
          {
            name: "Arfoud Voyages",
            body: "Nuestra agencia mayorista en Marruecos, especializada en diseñar experiencias a medida por todo el país.",
          },
          {
            name: "Xaluca Tours (Barcelona — Sabadell)",
            body: "Agencia minorista enfocada en viajes personalizados a Marruecos, con atención cercana y asesoramiento experto.",
          },
        ],
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────
  // Reservado para futuras incorporaciones de contenido.
  // Añadir nuevas categorías y secciones aquí a medida que el usuario
  // las comparta. Mantener `id`s únicos y en kebab-case.
  // ──────────────────────────────────────────────────────────────────
];
